import { ApiError } from "@wetalk/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Field, Input, Select } from "@wetalk/ui";
import { createContext, use, useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";

import { useGateway } from "../api/provider.js";
import type { AuthSessionView } from "../api/gateway.js";
import { authContextQuery, QUERY_KEY } from "../api/query.js";
import { apiProblemText } from "../api/api-error.js";
import { portalLocale, portalLocaleOf, setPortalLocale, usePortalTranslation, type PortalLocale } from "./portal.js";
import "./language-control.css";

const GUEST_KEY = "wetalk.portal-language.guest";
const personKey = (personId: string): string => `wetalk.portal-language.person.${personId}`;

function stored(key: string): PortalLocale | null {
  try { return portalLocaleOf(globalThis.localStorage.getItem(key)); }
  catch { return null; }
}
function remember(key: string, locale: PortalLocale): void {
  try { globalThis.localStorage.setItem(key, locale); }
  catch { /* Blocked browser storage does not prevent changing language. */ }
}
function browserLanguage(): PortalLocale {
  for (const language of globalThis.navigator.languages) {
    const supported = portalLocaleOf(language);
    if (supported !== null) return supported;
  }
  // Named initial interface language when none of the browser's languages is offered.
  return "en";
}
export async function initializePortalLocale(): Promise<void> {
  await setPortalLocale(stored(GUEST_KEY) ?? browserLanguage());
}

interface LanguageControl {
  readonly choose: (locale: PortalLocale) => void;
  readonly busy: boolean;
  /** A page that draws its own picker registers here, so the signed-out bar is not drawn twice; returns the release. */
  readonly hostPicker: () => () => void;
}
const LanguageContext = createContext<LanguageControl | null>(null);

/** Owns a person's preference independently of organization/workspace scope. */
export function PortalLanguageBoundary({ children }: { readonly children: ReactNode }): ReactNode {
  const gateway = useGateway();
  const queries = useQueryClient();
  const { locale, t } = usePortalTranslation("shell");
  useQuery(authContextQuery(gateway));
  // Auth adoption and logout can remove cache entries while this boundary stays mounted.
  // Subscribe to the authoritative cache rather than retaining a removed query observer.
  const current = useSyncExternalStore(
    useCallback((listener) => queries.getQueryCache().subscribe(listener), [queries]),
    useCallback(() => queries.getQueryData<AuthSessionView | null>(QUERY_KEY.authSession) ?? null, [queries]),
    () => null,
  );
  const subject = current?.actor.kind === "self" ? current.person.personId : null;
  const isSignedOut = current === null;
  const profile = useQuery({
    queryKey: ["portal-personal-locale", subject],
    queryFn: () => gateway.readMyProfile(),
    enabled: subject !== null,
    retry: false,
  });
  const adopted = useRef<string | null>(null);
  const editedFor = useRef<string | null>(null);
  const [challenge, setChallenge] = useState<{ personId: string; locale: PortalLocale } | null>(null);
  const [code, setCode] = useState("");
  const [factor, setFactor] = useState<"totp" | "recovery_code">("totp");
  const [problem, setProblem] = useState<unknown>(null);
  const [pending, setPending] = useState(false);
  const [stateSubject, setStateSubject] = useState(subject);
  const [hostedPickers, setHostedPickers] = useState(0);
  // Stable, so a page's registration effect runs once per mount rather than on every render here.
  const hostPicker = useCallback(() => {
    setHostedPickers((count) => count + 1);
    return () => { setHostedPickers((count) => count - 1); };
  }, []);

  // Reset preference-confirmation state with the identity transition, preserving child forms.
  if (stateSubject !== subject) {
    setStateSubject(subject);
    setChallenge(null);
    setProblem(null);
    setCode("");
    setPending(false);
  }

  function subjectIsActive(personId: string): boolean {
    const latest = queries.getQueryData<AuthSessionView | null>(QUERY_KEY.authSession);
    return latest?.actor.kind === "self" && latest.person.personId === personId;
  }

  useEffect(() => {
    editedFor.current = null;
    adopted.current = null;
    const next = subject === null ? stored(GUEST_KEY) : stored(personKey(subject));
    void setPortalLocale(next ?? browserLanguage());
  }, [subject]);

  useEffect(() => {
    if (subject === null || profile.data === undefined || adopted.current === subject || editedFor.current === subject) return;
    adopted.current = subject;
    const next = portalLocaleOf(profile.data.locale);
    if (next !== null) {
      remember(personKey(subject), next);
      void setPortalLocale(next);
    }
  }, [subject, profile.data]);

  useEffect(() => {
    globalThis.document.documentElement.lang = locale;
    if (isSignedOut) globalThis.document.title = "wetalk";
  }, [locale, isSignedOut]);

  async function save(personId: string, next: PortalLocale): Promise<void> {
    const saved = await gateway.updateMyLocale(next);
    if (!subjectIsActive(personId)) return;
    remember(personKey(personId), next);
    queries.setQueryData(["portal-personal-locale", personId], saved);
    setChallenge(null);
    setCode("");
  }

  function choose(next: PortalLocale): void {
    editedFor.current = subject;
    setProblem(null);
    void setPortalLocale(next);
    if (subject === null) {
      if (current?.actor.kind !== "operator") remember(GUEST_KEY, next);
      return;
    }
    const personId = subject;
    setPending(true);
    void save(personId, next).catch((error: unknown) => {
      if (!subjectIsActive(personId)) return;
      if (error instanceof ApiError && (error.code === "assurance_required" || error.code === "step_up_required")) {
        setChallenge({ personId, locale: next });
      } else setProblem(error);
    }).finally(() => { if (subjectIsActive(personId)) setPending(false); });
  }

  const confirm = useMutation({
    mutationFn: async () => {
      if (challenge === null || !subjectIsActive(challenge.personId)) return;
      await gateway.answerMfaChallenge(factor, code);
      if (!subjectIsActive(challenge.personId)) return;
      await save(challenge.personId, challenge.locale);
    },
    onError: (error) => {
      if (challenge !== null && subjectIsActive(challenge.personId)) setProblem(error);
    },
  });

  return <LanguageContext value={{ choose, busy: pending || confirm.isPending, hostPicker }}>
    {current === null && hostedPickers === 0 ? <div className="c-language-bar"><PortalLanguagePicker /></div> : null}
    {children}
    {problem !== null ? <p className="c-note c-note-critical c-language-problem" role="alert">
      {t("language.notSaved")} {apiProblemText(problem, t("language.failed"))}
    </p> : null}
    {challenge !== null ? <Dialog open title={t("language.confirmTitle")} onClose={() => { setChallenge(null); setCode(""); }}
      actions={<Button type="button" variant="primary" disabled={confirm.isPending || code.trim() === ""} onClick={() => { confirm.mutate(); }}>
        {t("language.save")}
      </Button>}>
      <p>{t("language.confirmNote")}</p>
      <Field label={t("language.factor")}>{(control) => <Select {...control} value={factor} onChange={(event) => { setFactor(event.target.value === "recovery_code" ? "recovery_code" : "totp"); }}>
        <option value="totp">{t("language.authenticator")}</option>
        <option value="recovery_code">{t("language.recovery")}</option>
      </Select>}</Field>
      <Field label={t("language.code")}>{(control) => <Input {...control} value={code} autoComplete="one-time-code" onChange={(event) => { setCode(event.target.value); }} />}</Field>
    </Dialog> : null}
  </LanguageContext>;
}

/**
 * The boundary's language switch for a page that draws its own control rather than `PortalLanguagePicker` — the public
 * header's flag menu on the way-in pages (`routes/auth/header-language-menu.tsx`, Claude Design's copy of 13 Oct 2026).
 * `null` outside the boundary, where `PortalLanguagePicker` draws nothing either.
 */
export function usePortalLanguageSwitch(): { readonly choose: (locale: PortalLocale) => void; readonly busy: boolean } | null {
  return use(LanguageContext);
}

/**
 * For a page that places `PortalLanguagePicker` in its own chrome (the public design's sign-in pages, ADR 0056 (portal amendment)):
 * while it is mounted the boundary draws no signed-out language bar above it.
 */
export function useHostedLanguagePicker(): void {
  const host = use(LanguageContext)?.hostPicker;
  useEffect(() => host?.(), [host]);
}

export function PortalLanguagePicker(): ReactNode {
  const control = use(LanguageContext);
  const { locale, t } = usePortalTranslation("shell");
  if (control === null) return null;
  return <label className="c-language-control">
    <span className="wt-visually-hidden">{t("language.label")}</span>
    <Select aria-label={t("language.label")} value={locale} disabled={control.busy} onChange={(event) => {
      const next = portalLocaleOf(event.target.value);
      if (next !== null && next !== portalLocale()) control.choose(next);
    }}>
      <option value="en" lang="en">English</option>
      <option value="el" lang="el">Ελληνικά</option>
    </Select>
  </label>;
}
